import { raise } from '@sparkdotfi/common-universal'

import { ApproveDelegationAction } from '../types'
import { getBorrowAllowanceQueryKey, readBorrowAllowance } from './query'
import { debtTokenAbi } from '@/config/abis/debtTokenAbi'
import { wethGatewayConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { ensureConfigTypes } from '@/features/actions/logic/transactions/useWrite'
import { ActionConfig, ActionContext } from '@/features/actions/logic/types'

export function createApproveDelegationActionConfig(
  action: ApproveDelegationAction,
  context: ActionContext,
): ActionConfig {
  const marketInfo = context.marketInfo ?? raise('Market info is required for approve delegation action')
  const { wagmiConfig, account, chainId } = context

  const wethGatewayAddress = getContractAddress(wethGatewayConfig.address, chainId)
  const reserve = marketInfo.findOneReserveBySymbol(action.token.symbol)
  const debtTokenAddress = reserve.variableDebtTokenAddress

  const borrowAllowanceOptions = {
    fromUser: account,
    toUser: wethGatewayAddress,
    wagmiConfig,
    debtTokenAddress,
    chainId,
  }

  return {
    getInitialParams: async () => {
      const data = await readBorrowAllowance(borrowAllowanceOptions)
      return { canBeSkipped: data >= action.token.toBigInt(action.value) }
    },

    getWriteConfig: () => {
      const value = action.token.toBigInt(action.value)

      return ensureConfigTypes({
        address: debtTokenAddress,
        abi: debtTokenAbi,
        functionName: 'approveDelegation',
        args: [wethGatewayAddress, value],
      })
    },

    verifyTransaction: async () => {
      const data = await readBorrowAllowance(borrowAllowanceOptions)
      return { success: data >= action.token.toBigInt(action.value) }
    },

    invalidates: () => [
      getBorrowAllowanceQueryKey({ fromUser: account, toUser: wethGatewayAddress, debtTokenAddress, chainId }),
    ],
  }
}
